import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import GeoContext from 'contexts/geoContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type {
  HotelSearchResultsData,
  ExperienceSearchResultsData,
  HotelSearchErrorData,
  ExperienceSearchErrorData,
  ChatUiActionData,
  ManualItineraryDeleteRequest,
  ManualItineraryMoveRequest,
} from 'tripPlanner/api/types'
import { useManualItineraryDeleteMutation, useManualItineraryMoveMutation } from 'tripPlanner/hooks/api/chat'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { resetPendingExperienceDaySelections, syncPendingExperienceDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { buildOfferIdContext, buildRoomOptionContext } from '../utils/messageEnrichment'
import type { UserData } from '../types'
import { useChatCore } from 'tripPlanner/assistant/runtime/chatStream'
import { useItineraryUpdates } from '../hooks/useItineraryUpdates'
import { useTripChatCartReconciliation } from '../hooks/useTripChatCartReconciliation'
import type { AnonymousClaimTransfer } from '../hooks/useAnonymousChatClaiming'
import { useTripPlannerEventBus, useEventSubscription } from './TripPlannerEventBus'
import { ChatActionsContext, type ChatActionsValue } from './ChatActionsContext'
import { ChatStreamStatusContext, type ChatStreamStatusValue } from './ChatStreamStatusContext'
import { ChatScrollContext, type ChatScrollValue } from './ChatScrollContext'
import { ChatUiActionContext, type ChatUiActionValue } from './ChatUiActionContext'
import { ChatStreamMessagesContext, type ChatStreamMessagesValue } from './ChatStreamMessagesContext'
import { ItineraryContext, type ItineraryContextValue } from './ItineraryContext'
import { ChatFilesContext, type ChatFilesValue } from './ChatFilesContext'

export interface TripPlannerChatProviderProps {
  chatId: number | undefined
  setChatIdForStreaming: (id: number | undefined) => void
  prompt: string | undefined
  isDevMode: boolean
  isLoggedIn: boolean
  orderIds?: Array<string>
  refetchChatList: () => Promise<unknown>
  waitForAnonymousChatClaim: (signal?: AbortSignal) => Promise<void>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  updateChatMetadata: (chatId: number, name: string, updatedAt: string) => void
  userData: UserData
  chatKey: string
  anonymousClaimTransfer: AnonymousClaimTransfer | undefined
  children: React.ReactNode
}

export function TripPlannerChatProvider({ children, ...props }: TripPlannerChatProviderProps) {
  const eventBus = useTripPlannerEventBus()
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const [files, setFiles] = useState<Array<File>>([])
  const { waitForAnonymousChatClaim } = props

  const isStreamingRef = useRef(false)

  const isOnline = useOnlineStatus()

  // ── Wire streaming callbacks ──

  const onHotelSearchResults = useCallback((data: HotelSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_results', data, generation })
  }, [eventBus])

  const onExperienceSearchResults = useCallback((data: ExperienceSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_results', data, generation })
  }, [eventBus])

  const onHotelSearchError = useCallback((data: HotelSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_error', data, generation })
  }, [eventBus])

  const onExperienceSearchError = useCallback((data: ExperienceSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_error', data, generation })
  }, [eventBus])

  // Buffer UI actions in state so they survive the landing→chat transition.
  // MapContainer may not be mounted when the first ui_action arrives (its event
  // subscription isn't active yet). The context-based buffer lets MapContainer
  // pick up the pending action on mount.
  const [pendingUiAction, setPendingUiAction] = useState<ChatUiActionData | undefined>(undefined)

  const clearPendingUiAction = useCallback(() => {
    setPendingUiAction(undefined)
  }, [])

  const onUiAction = useCallback((data: ChatUiActionData) => {
    setPendingUiAction(data)
    eventBus.dispatch({ type: 'ui_action', data })
  }, [eventBus])

  // ── Itinerary domain ──

  const {
    itineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    handleItineraryUpdates,
    handleEnrichmentComplete: handleItineraryEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  } = useItineraryUpdates({ chatId: props.chatId, isStreamingRef })

  // ── Chat core ──

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null)

  const {
    messages,
    filteredMessages,
    userFeedbackMap,
    setUserFeedbackMap,
    isChatError,
    chatError,
    loadingState,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    submitMessage,
    regenerateResponse,
    stopStreaming,
    retryLastMessage,
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    dynamicSpacerHeight,
    resetConversation,
    resetStreamingState,
    streamError,
    isStalled,
    reasoningSummaries,
    toolCallInfo,
    latestGenerationRef,
  } = useChatCore({
    chatId: props.chatId,
    setChatId: props.setChatIdForStreaming,
    prompt: props.prompt,
    isDevMode: props.isDevMode,
    files,
    setFiles,
    isLoggedIn: props.isLoggedIn,
    orderIds: props.orderIds,
    refetchChatList: props.refetchChatList,
    startChatTitleGeneration: props.startChatTitleGeneration,
    updateChatLastActivityTimestamp: props.updateChatLastActivityTimestamp,
    updateChatMetadata: props.updateChatMetadata,
    onItineraryUpdates: handleItineraryUpdates,
    onEnrichmentComplete: handleItineraryEnrichmentComplete,
    onStreamComplete: handleRefetchItinerary,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    messagesWrapperRef,
    userData: props.userData,
    surface: 'trip_planner',
    chatKey: props.chatKey,
  })

  // Sync isStreamingRef after render but before paint — useLayoutEffect eliminates the
  // 1-tick gap that would allow refetchOnWindowFocus to fire during streaming.
  useLayoutEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  // Single reset handler — placed after useChatCore so latestGenerationRef is in scope
  // Note: enrichment state (enrichingStopIds/enrichingItemIds) is derived from itinerary data
  // and auto-resets when chatId changes, so no manual reset is needed.
  useEventSubscription('reset_chat_state', () => {
    clearScrollTarget()
    resetPendingExperienceDaySelections()
    latestGenerationRef.current = 0
    setPendingUiAction(undefined)
    setFiles([])
  })

  // ── Chat message senders ──

  const sendMessage = useCallback(async(message: string, options?: SendMessageOptions) => {
    syncPendingExperienceDaySelection(options)

    let enrichedMessage = options?.offerId
      ? `${message}${buildOfferIdContext(options.offerId, options.offerType)}`
      : message

    if (options?.roomOption) {
      enrichedMessage = `${enrichedMessage}${buildRoomOptionContext(options.roomOption)}`
    }

    return submitMessage(enrichedMessage)
  }, [submitMessage])

  const sendPostAuthMessage = useCallback(async(signal?: AbortSignal) => {
    await waitForAnonymousChatClaim(signal)
    return sendMessage('I have logged in.')
  }, [sendMessage, waitForAnonymousChatClaim])

  // ── Memoized context values ──

  const actionsValue = useMemo<ChatActionsValue>(() => ({
    submitMessage,
    sendMessage,
    sendPostAuthMessage,
    regenerateResponse,
    stopStreaming,
    resetConversation,
    resetStreamingState,
    setFollowTail,
    retryLastMessage,
  }), [submitMessage, sendMessage, sendPostAuthMessage, regenerateResponse, stopStreaming, resetConversation, resetStreamingState, setFollowTail, retryLastMessage])

  const statusValue = useMemo<ChatStreamStatusValue>(() => ({
    chatId: props.chatId,
    chatKey: props.chatKey,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    loadingState,
    streamError,
    isStalled,
    isOnline,
    reasoningSummaries,
    toolCallInfo,
  }), [props.chatId, props.chatKey, isStreaming, isStreamOpen, isAwaitingMessageEnd, loadingState, streamError, isStalled, isOnline, reasoningSummaries, toolCallInfo])

  const scrollValue = useMemo<ChatScrollValue>(() => ({
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    dynamicSpacerHeight,
    messagesWrapperRef,
  }), [handleScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef])

  const uiActionValue = useMemo<ChatUiActionValue>(() => ({
    pendingUiAction,
    clearPendingUiAction,
  }), [pendingUiAction, clearPendingUiAction])

  const streamMessagesValue = useMemo<ChatStreamMessagesValue>(() => ({
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  }), [messages, filteredMessages, isChatError, chatError, userFeedbackMap, setUserFeedbackMap])

  const hasItineraryData = Boolean(itineraryData?.stops?.length)

  const multiCartId = itineraryData?.multiCartId

  const { mutateAsync: deleteManualItineraryTargetMutation } = useManualItineraryDeleteMutation()
  const { mutateAsync: moveManualItineraryItemsMutation } = useManualItineraryMoveMutation()
  const [isManualItineraryDeletePending, setIsManualItineraryDeletePending] = useState(false)
  const isManualItineraryDeletePendingRef = useRef(false)
  const [isManualItineraryMovePending, setIsManualItineraryMovePending] = useState(false)
  const isManualItineraryMovePendingRef = useRef(false)
  const [isFlightCartMutationPending, setFlightCartMutationPending] = useState(false)

  useTripChatCartReconciliation({
    chatId: props.chatId,
    itineraryData,
    isLoggedIn: props.isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    waitForAnonymousChatClaim,
    anonymousClaimTransfer: props.anonymousClaimTransfer,
  })

  const deleteManualItineraryTarget = useCallback(async(target: ManualItineraryDeleteRequest) => {
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return

    if (!props.chatId) {
      showSnackbar(intl.formatMessage({
        id: 'f5L_AqJEDPR7oRx_l8lK',
        defaultMessage: 'Unable to delete from itinerary. Please try again.',
        description: 'Error snackbar after manually deleting an item, stop, or day from a trip planner itinerary fails',
      }), 'critical')
      return
    }

    isManualItineraryDeletePendingRef.current = true
    setIsManualItineraryDeletePending(true)

    try {
      const result = await deleteManualItineraryTargetMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        target,
      })
      if (result.conversationContextSynced) {
        showSnackbar(intl.formatMessage({
          id: 'TRmwtLCL6KmMMN1QxBg6',
          defaultMessage: 'Removed from itinerary',
          description: 'Success snackbar after manually deleting an item, stop, or day from a trip planner itinerary',
        }), 'success')
      } else {
        showSnackbar(intl.formatMessage({
          id: '_pHdot57rf1emFxKUA-B',
          defaultMessage: 'Removed from itinerary, but AI context could not sync. Please refresh before continuing.',
          description: 'Critical snackbar after a manual itinerary delete succeeds but the AI conversation context update fails',
        }), 'critical')
      }
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'f5L_AqJEDPR7oRx_l8lK',
        defaultMessage: 'Unable to delete from itinerary. Please try again.',
        description: 'Error snackbar after manually deleting an item, stop, or day from a trip planner itinerary fails',
      }), 'critical')
    } finally {
      isManualItineraryDeletePendingRef.current = false
      setIsManualItineraryDeletePending(false)
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, deleteManualItineraryTargetMutation, intl])

  const moveManualItineraryItems = useCallback(async(request: ManualItineraryMoveRequest) => {
    const moveCount = request.moves.length
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return false

    if (!props.chatId) {
      showSnackbar(intl.formatMessage({
        id: 'niZexKiXSQvQU6UwoSLi',
        defaultMessage: 'Unable to move itinerary item. Please try again.',
        description: 'Error snackbar after manually moving an item to another day in a trip planner itinerary fails',
      }), 'critical')
      return false
    }

    isManualItineraryMovePendingRef.current = true
    setIsManualItineraryMovePending(true)

    try {
      const result = await moveManualItineraryItemsMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        request,
      })

      if (!result.success) {
        showSnackbar(intl.formatMessage({
          id: '44SsvAemYy2YP1WNK08K',
          defaultMessage: 'Some itinerary items could not be moved. Please try again.',
          description: 'Error snackbar after one or more manually moved trip planner itinerary items fail',
        }), 'critical')
        return false
      }

      if (result.conversationContextSynced) {
        showSnackbar(intl.formatMessage(
          {
            id: '0S0Cl33sBE6rr06ad_Pk',
            defaultMessage: 'Moved {moveCount, plural, one {itinerary item} other {itinerary items}}',
            description: 'Success snackbar after manually moving one or more trip planner itinerary items',
          },
          { moveCount },
        ), 'success')
      } else {
        showSnackbar(intl.formatMessage(
          {
            id: 'd90mGpew2mJSD8IuvHiz',
            defaultMessage: 'Moved {moveCount, plural, one {itinerary item} other {itinerary items}}, but AI context could not sync. Please refresh before continuing.',
            description: 'Critical snackbar after one or more manual itinerary moves succeed but AI conversation context sync fails',
          },
          { moveCount },
        ), 'critical')
      }
      return true
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'niZexKiXSQvQU6UwoSLi',
        defaultMessage: 'Unable to move itinerary item. Please try again.',
        description: 'Error snackbar after manually moving an item to another day in a trip planner itinerary fails',
      }), 'critical')
      return false
    } finally {
      isManualItineraryMovePendingRef.current = false
      setIsManualItineraryMovePending(false)
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, moveManualItineraryItemsMutation, intl])

  const itineraryValue = useMemo<ItineraryContextValue>(() => ({
    itineraryData,
    hasItineraryData,
    multiCartId,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    setFlightCartMutationPending,
  }), [
    itineraryData, hasItineraryData, multiCartId,
    enrichingStopIds, enrichingItemIds,
    scrollTarget, clearScrollTarget,
    refetchCurrentChatItinerary, deleteManualItineraryTarget, moveManualItineraryItems,
    isManualItineraryDeletePending, isManualItineraryMovePending, isFlightCartMutationPending,
  ])

  const filesValue = useMemo<ChatFilesValue>(() => ({
    files,
    setFiles,
  }), [files, setFiles])

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatStreamStatusContext.Provider value={statusValue}>
        <ChatScrollContext.Provider value={scrollValue}>
          <ChatFilesContext.Provider value={filesValue}>
            <ChatUiActionContext.Provider value={uiActionValue}>
              <ChatStreamMessagesContext.Provider value={streamMessagesValue}>
                <ItineraryContext.Provider value={itineraryValue}>
                  {children}
                </ItineraryContext.Provider>
              </ChatStreamMessagesContext.Provider>
            </ChatUiActionContext.Provider>
          </ChatFilesContext.Provider>
        </ChatScrollContext.Provider>
      </ChatStreamStatusContext.Provider>
    </ChatActionsContext.Provider>
  )
}
